import { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Spin,
  message,
  Button,
  Input,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import {
  ReloadOutlined,
  DownloadOutlined,
  SearchOutlined,
  MobileOutlined,
  SkinOutlined,
  ShoppingOutlined,
  FireOutlined,
  WalletOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import {
  getInventoryKpis,
  listInventoryPartners,
  exportInventory,
} from "../../services/inventory.service";
import { PartnerLabel } from "../../components/common/DeletedTag";
import StatusBadge from "../../components/common/StatusBadge";

const { Search } = Input;

function padCount(n) {
  const num = Number(n) || 0;
  return num < 10 ? `0${num}` : String(num);
}

function formatOmr(value) {
  const num = Number(value) || 0;
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function YesBadge({ value }) {
  if (!value) {
    return (
      <Tag
        style={{
          color: "#6b7280",
          background: "#f3f4f6",
          borderColor: "#e5e7eb",
          fontWeight: 700,
        }}
      >
        NO
      </Tag>
    );
  }
  return (
    <Tag
      style={{
        color: "#15803d",
        background: "#f0fdf4",
        borderColor: "#bbf7d0",
        fontWeight: 700,
      }}
    >
      YES
    </Tag>
  );
}

function KpiCard({ icon, label, value, currency }) {
  return (
    <div className="inventory-kpi-card">
      <div className="inventory-kpi-icon">{icon}</div>
      <div className="inventory-kpi-label">{label}</div>
      {currency ? (
        <div className="inventory-kpi-value-row">
          <span className="inventory-kpi-value inventory-kpi-value--money">
            {value}
          </span>
          <span className="inventory-kpi-currency">{currency}</span>
        </div>
      ) : (
        <div className="inventory-kpi-value">{value}</div>
      )}
    </div>
  );
}

/**
 * Inventory tab — filters + KPIs + partner allotment table (Figma 5:2493)
 */
export default function InventoryTab() {
  const navigate = useNavigate();

  const { options: projectOptions, loading: projectsLoading } =
    useSettingsDropdown("projects");
  const { options: fleetOptions, loading: fleetsLoading } = useSettingsDropdown(
    "fleet-vehicle-types",
    { filter: (item) => item.category === "fleet" }
  );
  const { options: cityOptions, loading: citiesLoading } = useSettingsDropdown(
    "city-zones",
    { filter: (item) => item.entityType === "city" }
  );
  const { options: zoneOptions, loading: zonesLoading } = useSettingsDropdown(
    "city-zones",
    { filter: (item) => item.entityType === "zone" }
  );

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total_count: 0,
  });
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({
    simCards: 0,
    uniforms: 0,
    deliveryBags: 0,
    fuelCards: 0,
    securityDepositTotal: 0,
    securityDepositRefunded: 0,
  });

  const queryParams = useCallback(
    (page = pagination.page) => ({
      page,
      per_page: pagination.per_page,
      search: search || undefined,
      ...filters,
    }),
    [pagination.page, pagination.per_page, search, filters]
  );

  const fetchAll = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = queryParams(page);
        const [kpiRes, listRes] = await Promise.all([
          getInventoryKpis(params),
          listInventoryPartners(params),
        ]);
        setKpis(kpiRes?.data || {});
        setRows(listRes?.data?.records || []);
        setPagination((prev) => ({
          ...prev,
          page,
          total_count: listRes?.data?.pagination?.total_count || 0,
        }));
      } catch {
        message.error("Failed to load inventory");
      } finally {
        setLoading(false);
      }
    },
    [queryParams]
  );

  useEffect(() => {
    fetchAll(1);
  }, [search, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === undefined || value === null || value === "") {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const handleExport = async () => {
    try {
      const raw = await exportInventory({
        search: search || undefined,
        ...filters,
      });
      const blob =
        raw instanceof Blob
          ? raw
          : new Blob([raw], { type: "text/csv;charset=utf-8" });
      downloadBlob(blob, `Inventory_${dayjs().format("YYYY-MM-DD")}.csv`);
      message.success("Download complete");
    } catch {
      message.error("Download failed");
    }
  };

  const cards = [
    {
      key: "sim",
      label: "TOTAL SIM CARDS",
      icon: <MobileOutlined />,
      value: padCount(kpis.simCards),
    },
    {
      key: "uniform",
      label: "TOTAL UNIFORMS",
      icon: <SkinOutlined />,
      value: padCount(kpis.uniforms),
    },
    {
      key: "bag",
      label: "TOTAL DELIVERY BAGS",
      icon: <ShoppingOutlined />,
      value: padCount(kpis.deliveryBags),
    },
    {
      key: "fuel",
      label: "TOTAL FUEL CARD",
      icon: <FireOutlined />,
      value: padCount(kpis.fuelCards),
    },
    {
      key: "deposit",
      label: "TOTAL SECURITY DEPOSIT",
      icon: <WalletOutlined />,
      value: formatOmr(kpis.securityDepositTotal),
      currency: "OMR",
    },
    {
      key: "refunded",
      label: "SECURITY DEPOSIT REFUNDED",
      icon: <UndoOutlined />,
      value: formatOmr(kpis.securityDepositRefunded),
      currency: "OMR",
    },
  ];

  const columns = [
    {
      title: "PARTNER ID",
      dataIndex: "riderId",
      key: "riderId",
      width: 140,
      sorter: (a, b) => String(a.riderId || "").localeCompare(String(b.riderId || "")),
      render: (v, record) => (
        <Button
          type="link"
          style={{ padding: 0, fontWeight: 600 }}
          onClick={() => navigate(`/delivery-partners/${record._id}`)}
        >
          <PartnerLabel name={v || "—"} deleted={record.riderDeleted || record.isDeleted} />
        </Button>
      ),
    },
    {
      title: "PARTNER NAME",
      dataIndex: "fullName",
      key: "fullName",
      width: 220,
      sorter: (a, b) => String(a.fullName || "").localeCompare(String(b.fullName || "")),
      render: (v, record) => (
        <strong>
          <PartnerLabel name={v} deleted={record.riderDeleted || record.isDeleted} />
        </strong>
      ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (s) => <StatusBadge status={s} />,
    },
    {
      title: "SD",
      dataIndex: "sd",
      key: "sd",
      width: 90,
      render: (v) => formatOmr(v),
    },
    {
      title: "SDR",
      dataIndex: "sdr",
      key: "sdr",
      width: 90,
      render: (v) => formatOmr(v),
    },
    {
      title: "SIM CARD",
      dataIndex: "hasSimCard",
      key: "hasSimCard",
      width: 100,
      render: (v) => <YesBadge value={v} />,
    },
    {
      title: "UNIFORM",
      dataIndex: "hasUniform",
      key: "hasUniform",
      width: 100,
      render: (v) => <YesBadge value={v} />,
    },
    {
      title: "DELIVERY BAG",
      dataIndex: "hasDeliveryBag",
      key: "hasDeliveryBag",
      width: 120,
      render: (v) => <YesBadge value={v} />,
    },
    {
      title: "FUEL CARD",
      dataIndex: "hasFuelCard",
      key: "hasFuelCard",
      width: 100,
      render: (v) => <YesBadge value={v} />,
    },
    {
      title: "VEHICLE",
      dataIndex: "vehicle",
      key: "vehicle",
      width: 140,
      ellipsis: true,
      render: (v) => v || "—",
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="inventory-filters">
        <Space wrap size="middle" style={{ marginBottom: 12 }}>
          <Search
            placeholder="Partner ID, Name..."
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={setSearch}
            style={{ width: 260 }}
          />
          <Select
            placeholder="Project"
            allowClear
            loading={projectsLoading}
            options={projectOptions}
            onChange={(v) => handleFilterChange("project", v)}
            style={{ width: 150 }}
          />
          <Select
            placeholder="Fleet Type"
            allowClear
            loading={fleetsLoading}
            options={fleetOptions}
            onChange={(v) => handleFilterChange("fleetType", v)}
            style={{ width: 140 }}
          />
          <Select
            placeholder="City"
            allowClear
            loading={citiesLoading}
            options={cityOptions}
            onChange={(v) => handleFilterChange("city", v)}
            style={{ width: 130 }}
          />
          <Select
            placeholder="Zones"
            allowClear
            loading={zonesLoading}
            options={zoneOptions}
            onChange={(v) => handleFilterChange("zone", v)}
            style={{ width: 130 }}
          />
        </Space>

        <Space style={{ marginBottom: 20 }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchAll(pagination.page)}
            loading={loading}
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            style={{ background: "#111827", borderColor: "#111827" }}
          >
            DOWNLOAD DATA
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          {cards.map((card) => (
            <Col key={card.key} xs={24} sm={12} lg={8}>
              <KpiCard
                icon={card.icon}
                label={card.label}
                value={card.value}
                currency={card.currency}
              />
            </Col>
          ))}
        </Row>

        <Table
          columns={columns}
          dataSource={rows}
          loading={loading}
          rowKey="_id"
          pagination={{
            current: pagination.page,
            pageSize: pagination.per_page,
            total: pagination.total_count,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `Showing ${range[0]} - ${range[1]} of ${total} records`,
            onChange: (page) => fetchAll(page),
          }}
          scroll={{ x: 1100 }}
        />
      </Spin>
    </div>
  );
}
